import { QueryClient, useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { Status, WaWiConnection } from "../../../../commons/libs/externals/wawi-proxy";
import * as wawiLib from "../../../../commons/libs/wawi-service";
import { WawiErrorType, WawiSettings } from "../../../../commons/types/wawi";
import useQuery, { UseQueryOptions } from "../hooks/use-query";

import { selectWawiTypeName } from "./use-wawi-connection.selectors";

const wawiConnectionInfoQueryKey = ["wawiConnectionInfo"] as const;
type WawiConnectionInfoQueryOptions = Omit<Partial<UseQueryOptions<WawiSettings>>, "queryKey" | "queryFn">;
export const useWawiConnectionInfo = (options: WawiConnectionInfoQueryOptions = {}) => {
  const query = useQuery({
    ...options,
    staleTime: 5 * 60 * 1000, // Can change upstream on the WaWi proxy without a client mutation.
    queryKey: wawiConnectionInfoQueryKey,
    queryFn: wawiLib.getWawiConnectionInfo
  });

  return {
    query,
    get initialized() {
      if (!query.data) {
        throw new Error(`
          'useWawiConnectionInfo': 'query.data' is still undefined! Please only use this selector after initialization.
          Please make sure that this hook is initialized during the initialization of the app. (useInitCore & useInitApp)
          `);
      } else {
        return query.data;
      }
    },
    get wawiType() {
      return this.initialized.type;
    },
    get isWawiConfigured() {
      return Boolean(this.initialized.type);
    },
    get wawiTypeName() {
      return selectWawiTypeName(this.initialized, wawiLib.WawiTypeLabel);
    }
  };
};

const onSuccessMutation = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: wawiConnectionInfoQueryKey });
  queryClient.invalidateQueries({ queryKey: wawiConnectionQueryKey });
};

const wawiConnectionQueryKey = ["wawiConnection"] as const;
type WawiConnectionQueryOptions = Omit<Partial<UseQueryOptions<WaWiConnection>>, "queryKey" | "queryFn">;
export const useWawiConnection = (options: WawiConnectionQueryOptions = {}) => {
  const query = useQuery({
    ...options,
    staleTime: 60 * 1000,
    queryKey: wawiConnectionQueryKey,
    queryFn: wawiLib.getWawiConnection
  });

  const isUnauthorized =
    query.error && query.error instanceof wawiLib.WawiError && query.error.wawiError === WawiErrorType.Unauthorized;

  return {
    query,
    isUnauthorized
  };
};

const createWawiConnectionQueryKey = ["createWawiConnection"] as const;
interface CreateWawiConnectionMutationVariables {
  wawiType: Parameters<typeof wawiLib.createWawiConnection>[0];
  wawiCredentials?: Parameters<typeof wawiLib.createWawiConnection>[1];
}
type CreateWawiConnectionMutationOptions = Omit<
  Partial<UseMutationOptions<WaWiConnection, Error, CreateWawiConnectionMutationVariables>>,
  "mutationKey" | "mutationFn"
>;
export const useCreateWawiConnection = (options: CreateWawiConnectionMutationOptions = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...options,
    mutationKey: createWawiConnectionQueryKey,
    mutationFn: ({ wawiType, wawiCredentials }) => wawiLib.createWawiConnection(wawiType, wawiCredentials),
    onSuccess: (data, variables, onMutateResult, context) => {
      onSuccessMutation(queryClient);
      options.onSuccess?.(data, variables, onMutateResult, context);
    }
  });

  return mutation;
};

const deleteWawiConnectionMutationKey = ["deleteWawiConnection"] as const;
type DeleteWawiConnectionMutationOptions = Omit<
  Partial<UseMutationOptions<Status, Error, void>>,
  "mutationKey" | "mutationFn"
>;
export const useDeleteWawiConnection = (options: DeleteWawiConnectionMutationOptions = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...options,
    mutationKey: deleteWawiConnectionMutationKey,
    mutationFn: wawiLib.deleteWawiConnection,
    onSuccess: (data, variables, onMutateResult, context) => {
      onSuccessMutation(queryClient);
      options.onSuccess?.(data, variables, onMutateResult, context);
    }
  });

  return mutation;
};

const resetWawiConnectionMutationKey = ["resetWawiConnection"] as const;
type ResetWawiConnectionMutationOptions = Omit<
  Partial<UseMutationOptions<void, Error, void>>,
  "mutationKey" | "mutationFn"
>;
export const useResetWawiConnection = (options: ResetWawiConnectionMutationOptions = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...options,
    mutationKey: resetWawiConnectionMutationKey,
    mutationFn: wawiLib.resetWawiConnection,
    onSuccess: (data, variables, onMutateResult, context) => {
      onSuccessMutation(queryClient);
      options.onSuccess?.(data, variables, onMutateResult, context);
    }
  });

  return mutation;
};
